import React, { useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { ArrowRight, AlertTriangle, Wrench, Clock, RefreshCw, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';

const BRANDS = {
  '/daikin-ac-problems': {
    name: 'Daikin',
    h1: 'Daikin AC Problems: Common Issues & When to Replace',
    overview: 'Daikin is a Japanese manufacturer and the largest HVAC company in the world. After acquiring Goodman in 2012, Daikin expanded significantly in the U.S. residential market. Daikin-branded units are known for inverter technology and high-efficiency ratings, but like all systems, they experience issues — especially in Louisiana\'s demanding climate.',
    parent: 'Daikin Industries',
    siblings: ['Goodman', 'Amana'],
    problems: [
      { title: 'Inverter Board Failures', desc: 'Daikin\'s variable-speed inverter boards can fail due to power surges common during Louisiana storms. Replacement boards are expensive ($800–$1,500) and may require factory-trained technicians.' },
      { title: 'Refrigerant Leaks', desc: 'Some Daikin units experience leaks at brazed joints on the evaporator coil, particularly in high-humidity environments where thermal cycling is constant.' },
      { title: 'PCB Error Codes', desc: 'The electronic control boards are sensitive to voltage fluctuations. Repeated error codes may indicate board degradation rather than a simple reset fix.' },
      { title: 'Condensate Drainage Issues', desc: 'In Louisiana\'s humidity, Daikin units produce significant condensate. Drain line clogs are common and can trigger safety shutdowns.' },
      { title: 'Outdoor Unit Corrosion', desc: 'Coastal salt air can corrode the aluminum fins and copper tubing faster than expected, especially within 20 miles of the Gulf.' },
    ],
    repairs: [
      'Inverter board replacement: $800–$1,500',
      'Evaporator coil replacement: $1,200–$2,500',
      'Control board replacement: $400–$900',
      'Refrigerant recharge (R-410A): $200–$600',
      'Condensate pump replacement: $150–$350',
    ],
    lifespan: '14–18 years nationally, 12–15 years in Louisiana due to extended cooling seasons and humidity stress.',
    replacement: 'If your Daikin unit is over 12 years old and experiencing inverter board or compressor issues, repair costs can quickly approach 40–60% of a new system. At that point, upgrading to a newer model with updated inverter technology and a fresh warranty is typically the better investment.',
  },
  '/goodman-ac-problems': {
    name: 'Goodman',
    h1: 'Goodman AC Problems: Common Issues & When to Replace',
    overview: 'Goodman is one of the most widely installed HVAC brands in the Southern United States, largely due to its affordable price point. Owned by Daikin Industries since 2012, Goodman units are manufactured in Houston, TX. While budget-friendly, they have known issues that Louisiana homeowners should be aware of.',
    parent: 'Daikin Industries',
    siblings: ['Daikin', 'Amana'],
    problems: [
      { title: 'Evaporator Coil Leaks', desc: 'Goodman units manufactured between 2004–2013 had widespread evaporator coil failures. While newer units have improved, coil leaks remain the most common Goodman complaint.' },
      { title: 'Capacitor Failures', desc: 'The run and start capacitors in Goodman units tend to fail more frequently than premium brands, especially during extreme heat when the system runs continuously.' },
      { title: 'Compressor Issues', desc: 'Some Goodman models use scroll compressors that can develop internal leaks. Compressor replacement on a Goodman unit typically costs $1,500–$2,500.' },
      { title: 'Contactor Burnout', desc: 'The electrical contactor can burn out from frequent cycling, causing the outdoor unit to stop receiving power.' },
      { title: 'Noise and Vibration', desc: 'Goodman condenser units are known to develop rattling and buzzing noises earlier than premium brands, often from loose mounting hardware or fan blade issues.' },
    ],
    repairs: [
      'Evaporator coil replacement: $1,000–$2,200',
      'Capacitor replacement: $150–$300',
      'Compressor replacement: $1,500–$2,500',
      'Contactor replacement: $100–$250',
      'Fan motor replacement: $300–$600',
    ],
    lifespan: '12–15 years nationally, 10–13 years in Louisiana. Goodman units tend to have shorter lifespans than premium brands due to component quality.',
    replacement: 'If your Goodman unit is over 10 years old and needs a major repair (coil or compressor), replacement is almost always more cost-effective. A new Goodman system costs $4,500–$7,000 installed, making it one of the most affordable replacement options.',
  },
  '/amana-ac-problems': {
    name: 'Amana',
    h1: 'Amana AC Problems: Common Issues & When to Replace',
    overview: 'Amana is Daikin\'s premium residential brand in the U.S., offering some of the best warranties in the industry including lifetime compressor warranties on select models. Amana units are manufactured alongside Goodman in Houston but use higher-grade components. Despite this, Louisiana\'s climate presents unique challenges.',
    parent: 'Daikin Industries',
    siblings: ['Daikin', 'Goodman'],
    problems: [
      { title: 'Warranty Claim Complexity', desc: 'While Amana offers excellent warranties, the claims process can be slow. Parts must be sourced through authorized channels, which can mean longer wait times during peak season.' },
      { title: 'Evaporator Coil Issues', desc: 'Like Goodman, older Amana units share similar coil designs that are prone to leaks. Newer models have improved, but units from 2006–2014 may still be affected.' },
      { title: 'Control Board Sensitivity', desc: 'The electronic control boards can be sensitive to power fluctuations from Louisiana storms, leading to intermittent operation or error codes.' },
      { title: 'Condensate Management', desc: 'High-efficiency Amana units produce more condensate in humid climates, and the drain systems can struggle to keep up during peak summer.' },
      { title: 'Compressor Hard Starts', desc: 'Older Amana units without a hard start kit can struggle to start in extreme heat, putting additional stress on the compressor.' },
    ],
    repairs: [
      'Evaporator coil replacement: $1,200–$2,500 (may be covered under warranty)',
      'Control board replacement: $400–$800',
      'Hard start kit installation: $100–$200',
      'Condensate pump: $150–$350',
      'Compressor replacement: $1,800–$3,000 (often warranty-covered)',
    ],
    lifespan: '15–20 years nationally, 12–16 years in Louisiana. Amana\'s premium components and warranty coverage can extend useful life compared to budget brands.',
    replacement: 'Thanks to Amana\'s lifetime compressor warranty, replacement is often delayed compared to other brands. However, if your system is over 14 years old and experiencing efficiency losses or non-warranty repairs exceeding $1,500, a new system with updated SEER ratings will save significantly on energy costs.',
  },
  '/trane-ac-problems': {
    name: 'Trane',
    h1: 'Trane AC Problems: Common Issues & When to Replace',
    overview: 'Trane is one of the most recognized HVAC brands in America, known for the slogan "It\'s Hard to Stop a Trane." Owned by Trane Technologies (formerly Ingersoll Rand), Trane manufactures both residential and commercial systems. They\'re popular in Louisiana due to their reputation for durability, but no system is immune to the Gulf Coast climate.',
    parent: 'Trane Technologies',
    siblings: ['American Standard', 'Oxbox'],
    problems: [
      { title: 'TXV (Thermostatic Expansion Valve) Failures', desc: 'Trane units commonly experience TXV failures, which cause the system to freeze up or blow warm air. This is one of the most frequently reported Trane issues.' },
      { title: 'Condenser Coil Corrosion', desc: 'Trane\'s Spine Fin coil design, while innovative, can collect debris and corrode in salt air environments near the Louisiana coast.' },
      { title: 'ComfortLink Control Issues', desc: 'Trane\'s proprietary ComfortLink communicating systems can experience communication errors between indoor and outdoor units, requiring specialized diagnosis.' },
      { title: 'High Repair Costs', desc: 'Trane parts tend to be more expensive than aftermarket alternatives, and many repairs require Trane-certified technicians.' },
      { title: 'Blower Motor Failures', desc: 'Variable-speed blower motors in higher-end Trane units can fail, with replacement costs running $600–$1,200.' },
    ],
    repairs: [
      'TXV replacement: $400–$800',
      'Condenser coil cleaning/replacement: $500–$2,800',
      'ComfortLink board repair: $500–$1,000',
      'Blower motor replacement: $600–$1,200',
      'Compressor replacement: $2,000–$3,500',
    ],
    lifespan: '15–20 years nationally, 12–16 years in Louisiana. Trane systems are built to last but still face accelerated wear from Gulf Coast conditions.',
    replacement: 'If your Trane unit is over 13 years old and facing a compressor or major coil replacement, the repair cost often exceeds 50% of a new system. Modern Trane units offer significantly higher SEER ratings that can reduce your cooling costs by 30–40%.',
  },
  '/american-standard-ac-problems': {
    name: 'American Standard',
    h1: 'American Standard AC Problems: Common Issues & When to Replace',
    overview: 'American Standard is essentially Trane\'s sister brand — both are manufactured by Trane Technologies using identical components and engineering. The main difference is distribution: American Standard is sold through independent dealers while Trane uses its own dealer network. If you have an American Standard unit, the problems and solutions mirror Trane exactly.',
    parent: 'Trane Technologies',
    siblings: ['Trane', 'Oxbox'],
    problems: [
      { title: 'TXV Failures', desc: 'Identical to Trane, American Standard units experience thermostatic expansion valve failures that cause freezing or warm air from vents.' },
      { title: 'Condenser Coil Issues', desc: 'Uses the same Spine Fin coil design as Trane. Salt air and debris accumulation affect performance, especially near the coast.' },
      { title: 'AccuLink Communication Errors', desc: 'American Standard\'s communicating system (AccuLink, equivalent to Trane\'s ComfortLink) can experience board communication failures between components.' },
      { title: 'Capacitor and Contactor Wear', desc: 'Extended run times in Louisiana summers accelerate wear on electrical components, requiring replacement every 5–8 years.' },
      { title: 'Warranty Parts Sourcing', desc: 'Some American Standard parts can take longer to source than Trane equivalents, depending on local dealer inventory.' },
    ],
    repairs: [
      'TXV replacement: $400–$800',
      'Condenser coil replacement: $500–$2,800',
      'AccuLink board repair: $500–$1,000',
      'Capacitor/contactor replacement: $150–$350',
      'Compressor replacement: $2,000–$3,500',
    ],
    lifespan: '15–20 years nationally, 12–16 years in Louisiana. Identical to Trane in durability and expected service life.',
    replacement: 'Same guidance as Trane: if repair costs exceed 50% of a new system\'s price on a unit older than 13 years, replacement is the smarter financial decision.',
  },
  '/oxbox-ac-problems': {
    name: 'Oxbox',
    h1: 'Oxbox AC Problems: Common Issues & When to Replace',
    overview: 'Oxbox is Trane Technologies\' budget-tier brand, designed for cost-conscious homeowners and builder-grade installations. While manufactured by the same parent company as Trane and American Standard, Oxbox uses simplified designs with fewer features to hit a lower price point. This means fewer advanced components to fail, but also fewer efficiency features.',
    parent: 'Trane Technologies',
    siblings: ['Trane', 'American Standard'],
    problems: [
      { title: 'Lower SEER Ratings', desc: 'Oxbox units typically offer lower efficiency ratings than Trane or American Standard, meaning higher operating costs in Louisiana\'s extended cooling season.' },
      { title: 'Basic Component Quality', desc: 'As a builder-grade brand, Oxbox uses more standard components that may not hold up as well to Louisiana\'s heat and humidity over time.' },
      { title: 'Limited Dealer Support', desc: 'Fewer dealers carry Oxbox parts compared to Trane, which can mean longer repair wait times.' },
      { title: 'Compressor Reliability', desc: 'The single-stage compressors in most Oxbox units work harder in extreme heat, leading to earlier compressor wear compared to variable-speed systems.' },
      { title: 'Noise Levels', desc: 'Without the sound-dampening features of premium Trane units, Oxbox condensers tend to be noticeably louder during operation.' },
    ],
    repairs: [
      'Compressor replacement: $1,500–$2,500',
      'Fan motor replacement: $300–$600',
      'Capacitor replacement: $100–$250',
      'Evaporator coil: $800–$1,800',
      'Contactor replacement: $100–$250',
    ],
    lifespan: '12–15 years nationally, 10–13 years in Louisiana. Shorter than Trane/American Standard due to fewer premium features and components.',
    replacement: 'If your Oxbox unit is over 10 years old and needs a compressor or coil replacement, upgrading to a higher-efficiency system will save more in monthly energy costs than the incremental investment. Oxbox repair costs are often close to replacement cost given the lower initial system price.',
  },
  '/carrier-ac-problems': {
    name: 'Carrier',
    h1: 'Carrier AC Problems: Common Issues & When to Replace',
    overview: 'Carrier is the founding name in modern air conditioning — Willis Carrier invented the technology in 1902. Today, Carrier Global Corporation manufactures residential and commercial HVAC systems known for reliability and innovation. Carrier is one of the most popular brands installed in Louisiana homes.',
    parent: 'Carrier Global',
    siblings: ['Bryant', 'Payne'],
    problems: [
      { title: 'Evaporator Coil Leaks', desc: 'Carrier experienced a significant coil issue in units manufactured roughly 2010–2015. Aluminum evaporator coils developed formicary corrosion, particularly in humid Southern climates.' },
      { title: 'Greenspeed Intelligence Issues', desc: 'Carrier\'s premium Greenspeed variable-speed units can experience communication errors between the thermostat and outdoor unit, requiring firmware updates or board replacement.' },
      { title: 'Scroll Compressor Noise', desc: 'Some Carrier scroll compressors develop a distinctive "chattering" noise at startup that worsens over time, indicating internal wear.' },
      { title: 'WeatherArmor Coating Degradation', desc: 'While Carrier\'s protective coating helps resist coastal corrosion, it can degrade after 8–10 years, exposing components to salt air damage.' },
      { title: 'Condensate Drain Placement', desc: 'Some Carrier air handler models have drain pan designs that are prone to standing water in high-humidity environments, promoting mold growth.' },
    ],
    repairs: [
      'Evaporator coil replacement: $1,200–$2,800',
      'Greenspeed control board: $600–$1,200',
      'Compressor replacement: $2,000–$3,500',
      'Blower motor: $500–$1,000',
      'Condensate pan/pump: $150–$400',
    ],
    lifespan: '15–20 years nationally, 12–16 years in Louisiana. Carrier systems with proper maintenance can be among the longest-lasting in the market.',
    replacement: 'If your Carrier is over 14 years old and needs a compressor or coil, replacement makes sense. Carrier offers the Infinity series with SEER ratings up to 26, which can dramatically reduce energy costs in Louisiana.',
  },
  '/bryant-ac-problems': {
    name: 'Bryant',
    h1: 'Bryant AC Problems: Common Issues & When to Replace',
    overview: 'Bryant is Carrier\'s sister brand, sharing the same manufacturing, engineering, and component designs. Bryant is distributed through independent dealers, while Carrier uses its own dealer network. A Bryant unit and a comparable Carrier unit are functionally identical — same compressor, same coils, same control boards.',
    parent: 'Carrier Global',
    siblings: ['Carrier', 'Payne'],
    problems: [
      { title: 'Evaporator Coil Corrosion', desc: 'Bryant shares the same coil designs as Carrier, including the aluminum coils that experienced formicary corrosion issues in units from 2010–2015.' },
      { title: 'Evolution System Communication', desc: 'Bryant\'s Evolution communicating system (equivalent to Carrier Greenspeed) can experience thermostat-to-unit communication failures.' },
      { title: 'Compressor Hard Starts', desc: 'In extreme Louisiana heat, Bryant compressors without hard start kits can struggle to start, leading to increased electrical draw and component stress.' },
      { title: 'Condensation Management', desc: 'Like Carrier, some Bryant air handler designs have drain pan configurations that can lead to standing water in high-humidity environments.' },
      { title: 'Part Availability Variance', desc: 'While parts are identical to Carrier, some local dealers may stock fewer Bryant-specific components, potentially extending repair times.' },
    ],
    repairs: [
      'Evaporator coil replacement: $1,200–$2,800',
      'Evolution control board: $600–$1,200',
      'Compressor replacement: $2,000–$3,500',
      'Hard start kit: $100–$200',
      'Blower motor: $500–$1,000',
    ],
    lifespan: '15–20 years nationally, 12–16 years in Louisiana. Identical to Carrier in expected service life.',
    replacement: 'Same as Carrier: if facing a major repair on a unit over 13 years old, the cost-benefit analysis typically favors replacement with a new, higher-efficiency system.',
  },
  '/payne-ac-problems': {
    name: 'Payne',
    h1: 'Payne AC Problems: Common Issues & When to Replace',
    overview: 'Payne is Carrier Global\'s budget-tier brand, commonly used in new construction and builder-grade installations. While manufactured by the same parent company as Carrier and Bryant, Payne offers fewer model options, lower SEER ratings, and more basic features at a lower price point.',
    parent: 'Carrier Global',
    siblings: ['Carrier', 'Bryant'],
    problems: [
      { title: 'Lower Efficiency', desc: 'Payne units typically offer SEER ratings of 13–16, which means higher operating costs compared to premium Carrier models rated 18–26 SEER.' },
      { title: 'Single-Stage Operation', desc: 'Most Payne systems are single-stage, meaning the compressor is either fully on or off. This leads to more temperature swings and humidity issues in Louisiana homes.' },
      { title: 'Earlier Component Wear', desc: 'Budget-tier components tend to wear faster under Louisiana\'s extended cooling season. Capacitors, contactors, and fan motors may need replacement sooner.' },
      { title: 'Coil Quality', desc: 'Payne uses the same coil technology as Carrier but in more basic configurations that may be more susceptible to corrosion in humid environments.' },
      { title: 'Limited Warranty Coverage', desc: 'Payne warranties are typically shorter than Carrier or Bryant, meaning out-of-pocket repair costs can add up faster.' },
    ],
    repairs: [
      'Compressor replacement: $1,500–$2,500',
      'Evaporator coil: $900–$2,000',
      'Capacitor/contactor: $100–$300',
      'Fan motor: $300–$550',
      'Refrigerant recharge: $200–$500',
    ],
    lifespan: '12–15 years nationally, 10–13 years in Louisiana. Shorter than premium Carrier/Bryant due to basic component selection.',
    replacement: 'If your Payne unit is over 10 years old and needs a compressor or coil replacement, upgrading to a mid-tier or premium system will pay for itself through energy savings within 3–5 years in Louisiana.',
  },
  '/rheem-ac-problems': {
    name: 'Rheem',
    h1: 'Rheem AC Problems: Common Issues & When to Replace',
    overview: 'Rheem is a well-known American HVAC manufacturer based in Atlanta, GA. They produce a full range of residential HVAC systems alongside water heaters and pool heaters. Rheem is widely available through independent dealers and big-box retailers, making them a popular choice for Louisiana homeowners.',
    parent: 'Rheem Manufacturing',
    siblings: ['Ruud'],
    problems: [
      { title: 'Scroll Compressor Failures', desc: 'Some Rheem models experience premature scroll compressor failure, particularly in units that run for extended periods during Louisiana summers.' },
      { title: 'Evaporator Coil Leaks', desc: 'Rheem\'s aluminum evaporator coils can develop micro-leaks in humid climates, leading to slow refrigerant loss and reduced cooling capacity.' },
      { title: 'EcoNet Smart Thermostat Issues', desc: 'Rheem\'s proprietary EcoNet system can experience connectivity issues and software glitches that affect system operation.' },
      { title: 'Condenser Fan Motor Failures', desc: 'Fan motors in Rheem outdoor units tend to fail after 8–10 years, especially with constant use in hot climates.' },
      { title: 'Rust and Corrosion', desc: 'The cabinet and base pan of Rheem outdoor units can rust in coastal Louisiana environments faster than units with full galvanized steel construction.' },
    ],
    repairs: [
      'Compressor replacement: $1,800–$3,000',
      'Evaporator coil: $1,000–$2,500',
      'Fan motor: $300–$700',
      'EcoNet board replacement: $400–$800',
      'Capacitor replacement: $100–$250',
    ],
    lifespan: '15–18 years nationally, 12–15 years in Louisiana. Mid-range durability with proper maintenance.',
    replacement: 'If your Rheem unit is over 12 years old and experiencing compressor or coil issues, modern Rheem systems with higher SEER ratings (up to 20) can reduce your cooling costs by 25–40% while starting a new warranty period.',
  },
  '/ruud-ac-problems': {
    name: 'Ruud',
    h1: 'Ruud AC Problems: Common Issues & When to Replace',
    overview: 'Ruud is Rheem\'s sister brand — both are manufactured by Rheem Manufacturing using identical components, designs, and engineering. The primary difference is distribution: Ruud is sold through plumbing and HVAC wholesale channels. If you have a Ruud unit, everything about it — from problems to parts — is identical to the equivalent Rheem model.',
    parent: 'Rheem Manufacturing',
    siblings: ['Rheem'],
    problems: [
      { title: 'Scroll Compressor Issues', desc: 'Identical to Rheem — some models experience premature compressor wear under extended operation typical of Louisiana summers.' },
      { title: 'Evaporator Coil Leaks', desc: 'Same aluminum coil design as Rheem. Micro-leaks can develop in humid climates, causing gradual refrigerant loss.' },
      { title: 'Condenser Fan Motor Wear', desc: 'Fan motors wear out at the same rate as Rheem units, typically needing replacement after 8–10 years of heavy use.' },
      { title: 'Cabinet Corrosion', desc: 'The outdoor unit housing is susceptible to rust in coastal environments, same as Rheem.' },
      { title: 'Part Availability', desc: 'While parts are identical to Rheem, some contractors may not stock Ruud-branded components, though any Rheem part will fit.' },
    ],
    repairs: [
      'Compressor replacement: $1,800–$3,000',
      'Evaporator coil: $1,000–$2,500',
      'Fan motor: $300–$700',
      'Capacitor replacement: $100–$250',
      'Contactor replacement: $100–$250',
    ],
    lifespan: '15–18 years nationally, 12–15 years in Louisiana. Identical to Rheem.',
    replacement: 'Same guidance as Rheem: if repair costs on a 12+ year-old unit approach $2,000+, a new system is the better long-term investment.',
  },
  '/york-ac-problems': {
    name: 'York',
    h1: 'York AC Problems: Common Issues & When to Replace',
    overview: 'York is one of the oldest HVAC brands in America, now owned by Johnson Controls. York manufactures both residential and commercial systems and is particularly well-known in the commercial sector. In the residential market, York competes in the mid-tier segment with solid but not premium components.',
    parent: 'Johnson Controls',
    siblings: ['Luxaire', 'Coleman'],
    problems: [
      { title: 'Compressor Reliability', desc: 'Some York residential models use compressors that are more suited to moderate climates and can struggle with the continuous demand of Louisiana summers.' },
      { title: 'Control Board Failures', desc: 'York\'s Affinity and LX series control boards can fail from power surges or lightning strikes common in Louisiana thunderstorm season.' },
      { title: 'Condenser Coil Design', desc: 'York\'s condenser coil configuration can trap debris more easily than competing designs, reducing efficiency if not cleaned regularly.' },
      { title: 'Limited Dealer Network', desc: 'York has fewer authorized residential dealers in Louisiana compared to Carrier, Trane, or Rheem, which can affect service availability and parts access.' },
      { title: 'Thermostat Compatibility', desc: 'York\'s communicating systems sometimes have compatibility issues with popular third-party thermostats like Nest or Ecobee.' },
    ],
    repairs: [
      'Compressor replacement: $1,800–$3,200',
      'Control board: $400–$900',
      'Condenser coil cleaning/replacement: $400–$2,500',
      'Fan motor: $350–$700',
      'Refrigerant recharge: $200–$600',
    ],
    lifespan: '14–18 years nationally, 11–14 years in Louisiana. York systems with regular maintenance perform well but may have shorter residential lifespans than Carrier or Trane.',
    replacement: 'If your York unit is over 12 years old and needs a major component, consider that York residential parts can be harder to source locally. Replacement with a more widely-supported brand may provide better long-term service access in Louisiana.',
  },
};

const BrandProblemPage = () => {
  const location = useLocation();
  const data = BRANDS[location.pathname];

  const relatedBrands = useMemo(() => {
    if (!data) return [];
    return Object.entries(BRANDS)
      .filter(([path, b]) => b.parent === data.parent && path !== location.pathname);
  }, [location.pathname, data]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white">
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm font-medium text-[#0EA5E9] mb-3 uppercase tracking-wide">
            {data.parent} &middot; {data.name}
          </p>
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="brand-page-heading"
          >
            {data.h1}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">{data.overview}</p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Get an Instant HVAC Replacement Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Brand Family Note */}
      <section className="py-4">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600">
              <strong className="text-[#0F172A]">{data.name}</strong> is manufactured by{' '}
              <strong>{data.parent}</strong>, which also makes{' '}
              {data.siblings.join(' and ')}.{' '}
              <a href="/who-makes-hvac-systems" className="text-[#0EA5E9] hover:underline">
                Learn about HVAC brand ownership
              </a>.
            </p>
          </div>
        </div>
      </section>

      {/* Common Problems */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <AlertTriangle className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Common {data.name} AC Problems
            </h2>
          </div>
          <div className="space-y-4">
            {data.problems.map((p, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-6" data-testid={`brand-problem-${i}`}>
                <h3 className="font-bold text-[#0F172A] mb-1">{p.title}</h3>
                <p className="text-slate-600 text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Typical Repairs */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <Wrench className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Typical Repair Costs
            </h2>
          </div>
          <div className="space-y-3">
            {data.repairs.map((r, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl">
                <span className="w-2 h-2 rounded-full bg-[#F97316] flex-shrink-0" />
                <span className="text-sm text-[#0F172A]">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lifespan */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Expected Lifespan in Louisiana
            </h2>
          </div>
          <div className="bg-slate-50 rounded-2xl p-8">
            <p className="text-slate-600 text-lg" data-testid="brand-lifespan">{data.lifespan}</p>
          </div>
        </div>
      </section>

      {/* When to Replace */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <RefreshCw className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              When Replacement Is Recommended
            </h2>
          </div>
          <div className="bg-[#FFF7ED] border border-orange-200 rounded-2xl p-8">
            <p className="text-slate-700 leading-relaxed" data-testid="brand-replacement">{data.replacement}</p>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="prose prose-slate max-w-none">
            <p>
              Want to understand how {data.name} compares to other brands?{' '}
              <a href="/who-makes-hvac-systems" className="text-[#0EA5E9]">See who makes HVAC systems</a>.
              Ready to explore your options? Get a free{' '}
              <a href="/instant-hvac-estimate-new-orleans" className="text-[#0EA5E9]">instant HVAC estimate</a>{' '}
              or check{' '}
              <a href="/ac-replacement-cost-new-orleans" className="text-[#0EA5E9]">AC replacement costs in New Orleans</a>.
              Not sure of your unit's age?{' '}
              <a href="/check-ac-age" className="text-[#0EA5E9]">Decode your serial number</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Related Brands */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <h3 className="font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Related Brands ({data.parent})
          </h3>
          <div className="flex flex-wrap gap-3">
            {relatedBrands.map(([path, b]) => (
                <a
                  key={path}
                  href={path}
                  className="px-4 py-2 bg-white rounded-full border border-slate-200 text-sm font-medium text-[#0F172A] hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors"
                >
                  {b.name} AC Problems
                </a>
              ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Get an Instant HVAC Replacement Estimate
          </h2>
          <p className="text-slate-300 mb-8">
            See what a new system would cost for your home — free, fast, and no obligation.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Start My Instant Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BrandProblemPage;
